import React, { useState } from 'react';
import { Button, Message, SearchableSingleSelect, SelectOption, TextInput } from 'common.ui';
import { devToolsApi } from 'api/AdgangClients';
import { ImportUserRequest, OrganizationResponse, UserType } from 'api/adgang';
import { useApplications, useRoles } from 'api/hooks';
import { useOrganizations } from 'api/hooks/useOrganizations';

interface UserImportFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  roleId: string;
  organizationId: string;
}

interface ImportResponse {
  correlationId: string;
  acceptedCount?: number;
  errors?: Array<{ rowIndex: number; reason: string }>;
}

const emptyForm: UserImportFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  roleId: '',
  organizationId: '',
};

function UserImportForm() {
  const [formData, setFormData] = useState<UserImportFormData>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<ImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Importen lager eksterne brukere, og valideres mot Adgang-appens roller (vi
  // tester som Adgang). Scoper rolle-listen til eksterne roller på Adgang-appen.
  const [, applications] = useApplications();
  const adgangApplicationId = applications?.find(a => a.shortName === 'Adgang')?.id ?? undefined;
  const [roleOptions, roleData] = useRoles(UserType.External, adgangApplicationId);
  const [organizationGroupIds, setOrganizationGroupIds] = useState<number[] | null | undefined>();
  const [organizations] = useOrganizations(organizationGroupIds ?? [], [organizationGroupIds]);

  const setField = (name: keyof UserImportFormData) => (value?: string | null) => {
    setFormData(prev => ({ ...prev, [name]: value ?? '' }));
  };

  const onRoleChange = (roleId: string) => {
    // Bytter rolle: nullstill org-valg og hent org-gruppene rollen ev. krever.
    const selectedRole = roleData?.find(r => r.roleId?.toString() === roleId);
    setOrganizationGroupIds(selectedRole?.organizationGroupIds);
    setFormData(prev => ({ ...prev, roleId, organizationId: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const importRequest: ImportUserRequest = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber || null,
        roleId: parseInt(formData.roleId, 10),
        organizationId: formData.organizationId ? parseInt(formData.organizationId, 10) : null,
      };

      const resp = await devToolsApi.apiDevToolsImportUserPost({ importUserRequest: importRequest });

      setResponse(resp as ImportResponse);
      setFormData(emptyForm);
      setOrganizationGroupIds(undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'En feil oppstod under import');
    } finally {
      setLoading(false);
    }
  };

  const showOrganizationDropdown =
    organizationGroupIds !== undefined && organizationGroupIds !== null && organizationGroupIds.length > 0;

  const organizationOptions: SelectOption[] = (organizations ?? []).map((org: OrganizationResponse) => ({
    id: org.organizationId?.toString() ?? '',
    text: org.name ?? '',
  }));

  const successText = response
    ? [
        `Korrelasjon-ID: ${response.correlationId}`,
        ...(response.acceptedCount !== undefined ? [`Godtatt: ${response.acceptedCount}`] : []),
      ]
    : [];

  return (
    <div style={{ maxWidth: '480px' }}>
      <h3>Brukerimport - Test</h3>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <TextInput id='firstName' labelText='Fornavn' value={formData.firstName} onChange={setField('firstName')} />
        <TextInput id='lastName' labelText='Etternavn' value={formData.lastName} onChange={setField('lastName')} />
        <TextInput id='email' labelText='E-post' value={formData.email} onChange={setField('email')} />
        <TextInput
          id='phoneNumber'
          labelText='Telefon (valgfritt)'
          value={formData.phoneNumber}
          onChange={setField('phoneNumber')}
        />

        <SearchableSingleSelect
          id='roleId'
          label='Rolle'
          selectedId={formData.roleId}
          options={roleOptions ?? []}
          onChange={onRoleChange}
        />

        {showOrganizationDropdown && (
          <SearchableSingleSelect
            id='organizationId'
            label='Organisasjon'
            selectedId={formData.organizationId}
            options={organizationOptions}
            onChange={setField('organizationId')}
          />
        )}

        <div style={{ marginTop: '8px' }}>
          <Button type='submit' text='Importer bruker' loading={loading} disabled={loading} />
        </div>
      </form>

      {error && (
        <div style={{ marginTop: '20px' }}>
          <Message type='error' text={error} />
        </div>
      )}

      {response && (
        <div style={{ marginTop: '20px' }}>
          <Message type='confirmation' text={successText} />
          {response.errors && response.errors.length > 0 && (
            <Message type='warning' text={response.errors.map(err => `Rad ${err.rowIndex}: ${err.reason}`)} />
          )}
        </div>
      )}
    </div>
  );
}

export default UserImportForm;
